import { resolveAgentFlightDisplayedSortPrice } from "../../../../Components/FlightBookingFlowMockup/flightBookingMockup/resolveAgentFlightDisplayedSortPrice";
import { legacyAppRound2 } from "../../../../pricing/pricingEngine.js";

/**
 * BookingPriceViewModel — display mapping only (no new math).
 *
 * pricingFxRate: transient quote FX (never labeled "snapshot" in UI).
 * bookingExchangeRateSnapshot: persisted only; null if absent.
 *
 * @param {{
 *   offer?: object|null,
 *   pricing?: object|null,
 *   bookingExchangeRateSnapshot?: number|null,
 * }} input
 * @returns {import("../types").BookingPriceViewModel}
 */
export function mapBookingPriceViewModel(input = {}) {
  const offer = input.offer || null;
  const pricing = input.pricing || null;

  const managed = pricing?.managedOurTotalFare || null;
  const ourTotal =
    offer?.rawDeal?.ourPrice?.ourTotalFare ||
    managed ||
    null;

  const pricingFxRate = firstPositive(
    ourTotal?.fx_rate,
    offer?.priceTrace?.fxRate,
    offer?.fx_rate
  );

  const bookingExchangeRateSnapshot =
    input.bookingExchangeRateSnapshot != null &&
    Number.isFinite(Number(input.bookingExchangeRateSnapshot))
      ? Number(input.bookingExchangeRateSnapshot)
      : null;

  // Source amount and its currency must come from the same offer field.
  // pricing.currency on the LDS path is the ILS settlement currency, not the fare currency.
  const source = takeSourceAmount(offer, pricing);
  const sourceCurrency = source.currency;
  const sourceCurrencyTotal = source.amount;
  // Customer flight total in source currency. Never supplier rawTotalFare.
  const clientFlightTotal = readClientFlightTotal(offer, pricing, sourceCurrency);

  const flightBaseTotal = finiteOrNull(
    pricing?.flight ?? pricing?.base ?? offer?.priceClientTotalIls
  );
  const extrasTotal = finiteOrNull(pricing?.extras);
  const baggageTotal =
    finiteOrNull(pricing?.baggageTotal) ??
    (extrasTotal != null ? extrasTotal : null);
  // Denomination already used by computeFlightBookingPricing — not a conversion.
  // LDS / offer fallback: pricing.extras is computeExtrasTotalIls (ILS).
  // provider_native: extras are added only in pricing.currency.
  const extrasCurrency =
    extrasTotal == null
      ? null
      : pricing?.source === "provider_native"
        ? normalizeCurrencyCode(pricing?.currency) || sourceCurrency
        : "ILS";

  const clientIls = finiteOrNull(offer?.priceClientTotalIls);
  const settlementIls = pricing?.currency === "ILS" || pricing?.currency === "₪";
  const rawCalculatedIls =
    clientIls ?? (settlementIls ? finiteOrNull(pricing?.total) : null);
  // One ceil for the agent. legacyAppRound2 is Math.ceil. Do not ceil again in the summary.
  const calculatedIlsTotal =
    rawCalculatedIls == null ? null : legacyAppRound2(rawCalculatedIls);
  // Engine charge. Payment reads this; the summary does not re-ceil it.
  const payableIlsTotal =
    settlementIls || !pricing?.currency ? finiteOrNull(pricing?.total) : null;
  const handlingFeeAmount = finiteOrNull(pricing?.handlingFee);
  const feeAdd =
    handlingFeeAmount != null && handlingFeeAmount !== 0 ? handlingFeeAmount : 0;
  // Flight + fee when the customer ILS total is the base. That figure has no extras.
  const flightPlusFee =
    calculatedIlsTotal == null
      ? null
      : clientIls != null
        ? calculatedIlsTotal + feeAdd
        : calculatedIlsTotal;
  // pricing.total already includes priced extras. Use it when subtracting those
  // extras lands on the flight+fee figure. Do not add extras again here.
  const extrasAmount = extrasTotal != null && extrasTotal > 0 ? extrasTotal : 0;
  const engineIncludesExtras =
    extrasAmount > 0 &&
    payableIlsTotal != null &&
    flightPlusFee != null &&
    Math.abs(payableIlsTotal - extrasAmount - flightPlusFee) <= 1.5;
  const summaryPayableIlsTotal = engineIncludesExtras ? payableIlsTotal : flightPlusFee;

  // Display rounding lives in existing resolver — expose as display only; do not invent adjustment field math
  const display = offer ? resolveAgentFlightDisplayedSortPrice(offer) : null;

  return {
    sourceCurrency,
    flightBaseTotal,
    baggageTotal,
    extrasTotal,
    extrasCurrency,
    sourceCurrencyTotal,
    clientFlightTotal,
    pricingFxRate,
    bookingExchangeRateSnapshot,
    calculatedIlsTotal,
    roundingAdjustment: null,
    payableIlsTotal,
    summaryPayableIlsTotal,
    handlingFee: finiteOrNull(pricing?.handlingFee),
    creditCardFee: finiteOrNull(pricing?.creditCardFee),
    passengerPriceBreakdown: buildPassengerBreakdown(offer),
    baggageBreakdown: null,
    commission: mapCommission(ourTotal, offer),
    pricingSource: strOrNull(pricing?.source) || null,
    display,
  };
}

function buildPassengerBreakdown(offer) {
  if (!offer) return null;
  const ils = finiteOrNull(offer.priceClientPerPersonIls);
  const usd = finiteOrNull(offer.priceClientPerPersonUsd);
  if (ils == null && usd == null) return null;
  return {
    perPersonIls: ils,
    perPersonUsd: usd,
    totalIls: finiteOrNull(offer.priceClientTotalIls),
    totalPaxCount: finiteOrNull(offer.totalPaxCount),
  };
}

function mapCommission(ourTotal, offer) {
  const c =
    ourTotal?.commission ??
    offer?.rawDeal?.fareDetails?.commission ??
    null;
  if (c == null || typeof c !== "object") return null;
  return { ...c };
}

/**
 * Customer flight total in source currency.
 * 1. priceClientTotalUsd (ILS source: stored priceClientTotalIls)
 * 2. Sum of per-passenger client amounts when that list is complete
 * 3. perPerson × pax when this is the customer per-person SSOT (the figure behind priceClientTotalIls).
 *    Skipped when paxCounts prove mixed fares and no customer total or per-pax list exists.
 * Never rawTotalFare.
 */
function readClientFlightTotal(offer, pricing, sourceCurrency) {
  const c = normalizeCurrencyCode(sourceCurrency);

  if (c === "ILS") {
    const ils =
      finiteOrNull(offer?.priceClientTotalIls) ??
      finiteOrNull(pricing?.priceClientTotalIls);
    if (ils != null) return ils;
  } else {
    const usd =
      finiteOrNull(offer?.priceClientTotalUsd) ??
      finiteOrNull(pricing?.priceClientTotalUsd);
    if (usd != null) return usd;
  }

  const summed = sumClientPaxAmounts(offer, c);
  if (summed != null) return summed;

  if (isProvenMixedFare(offer)) return null;
  const perPerson =
    c === "ILS"
      ? finiteOrNull(offer?.priceClientPerPersonIls)
      : finiteOrNull(offer?.priceClientPerPersonUsd);
  const pax = finiteOrNull(offer?.totalPaxCount);
  if (perPerson != null && pax != null && pax > 0) return perPerson * pax;
  return null;
}

function sumClientPaxAmounts(offer, currency) {
  const raw = offer?.passengerPriceBreakdown;
  const rows = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.passengers)
      ? raw.passengers
      : null;
  if (!rows || rows.length === 0) return null;
  let sum = 0;
  for (const row of rows) {
    const n =
      currency === "ILS"
        ? finiteOrNull(row?.priceIls ?? row?.amountIls ?? row?.perPersonIls)
        : finiteOrNull(row?.priceUsd ?? row?.amountUsd ?? row?.perPersonUsd ?? row?.amount);
    if (n == null) return null;
    sum += n;
  }
  return sum;
}

function isProvenMixedFare(offer) {
  const counts = offer?.paxCounts;
  if (!counts || typeof counts !== "object") return false;
  const adults = finiteOrNull(counts.adults) ?? 0;
  const children = finiteOrNull(counts.children ?? counts.kids) ?? 0;
  const infants = finiteOrNull(counts.infants) ?? 0;
  const kinds = [adults > 0, children > 0, infants > 0].filter(Boolean).length;
  return kinds > 1;
}

function firstPositive(...vals) {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/**
 * First positive fare amount, with the currency stored on that same field.
 * Does not borrow pricing.currency (ILS settlement) for a USD fare total.
 */
function takeSourceAmount(offer, pricing) {
  const candidates = [
    [offer?.resolvedPrice?.amount, offer?.resolvedPrice?.currency],
    [offer?.providerPriceAmount, offer?.providerPriceCurrency],
    [offer?.price?.amount, offer?.price?.currency],
    [offer?.rawTotalFare, offer?.rawCurrency],
  ];
  if (pricing?.source === "provider_native") {
    candidates.push([pricing?.total, pricing?.currency]);
  }
  for (const [amount, currency] of candidates) {
    const n = Number(amount);
    if (Number.isFinite(n) && n > 0) {
      return { amount: n, currency: normalizeCurrencyCode(currency) };
    }
  }
  return { amount: null, currency: null };
}

function normalizeCurrencyCode(raw) {
  if (raw == null || typeof raw === "object") return null;
  const c = String(raw).trim().toUpperCase();
  if (!c || c === "[OBJECT OBJECT]") return null;
  if (c === "$" || c === "US$") return "USD";
  if (c === "₪" || c === "NIS") return "ILS";
  return c;
}

function finiteOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function strOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s || null;
}
